import { MutableRefObject, useRef } from "react"
import { createContext, useContextSelector } from "use-context-selector"
import GalleryCarousel from "./GalleryCarousel"
import GalleryGrid from "./GalleryGrid"
import { CarouselProps, GalleryProps, GridProps } from "./types"

export interface GalleryContextType<T> extends GalleryProps<T> {
  itemRefs: MutableRefObject<HTMLDivElement[]>
}

export const GalleryContext = createContext<GalleryContextType<any>>(null!)

export function useGalleryContext(): GalleryContextType<any>
export function useGalleryContext<S>(
  selector: (value: GalleryContextType<any>) => S
): S
export function useGalleryContext<S>(
  selector?: (value: GalleryContextType<any>) => S
): S | GalleryContextType<any> {
  if (selector) {
    return useContextSelector(GalleryContext, selector)
  }
  return useContextSelector(GalleryContext, (v) => v)
}

const Gallery = <T,>(props: GalleryProps<T>) => {
  // We store a ref for each item in the gallery and pass them via context
  const itemRefs = useRef<HTMLDivElement[]>([])

  // Set default values for optional props
  const passedProps = {
    ...props,
    spacingX: props.spacingX || "2rem",
    spacingY: props.spacingY || "2rem",
    gap: props.gap || 0,
    maxItemHeight: props.maxItemHeight,
    itemsPerRow: props.itemsPerRow || 3,
    carouselPeek: props.carouselPeek ?? true,
    scrollBehavior: props.scrollBehavior || "snap",
    itemsFillWidth: props.itemsFillWidth ?? true,
    enableStagger: props.enableStagger ?? false
  }

  const contextValue = {
    ...passedProps,
    itemRefs
  }

  return (
    <GalleryContext.Provider value={contextValue}>
      {props.mode === "carousel" ? (
        <GalleryCarousel {...passedProps} />
      ) : (
        <GalleryGrid {...passedProps} />
      )}
    </GalleryContext.Provider>
  )
}

export const CarouselGallery = <T,>(props: CarouselProps<T>) => {
  return <Gallery {...props} mode="carousel" />
}

export const GridGallery = <T,>(props: GridProps<T>) => {
  return <Gallery {...props} mode="grid" />
}

export default Gallery
